import type { RiskLevel } from '@nestjs-boilerplate/types';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface RiskLevelFilterProps {
  value: RiskLevel[];
  onChange: (value: RiskLevel[]) => void;
}

const riskLevels: { value: RiskLevel; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

export function RiskLevelFilter({ value, onChange }: RiskLevelFilterProps) {
  const handleToggle = (level: RiskLevel) => {
    if (value.includes(level)) {
      onChange(value.filter((v) => v !== level));
    } else {
      onChange([...value, level]);
    }
  };

  const selectedCount = value.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="justify-between">
          <span>Client Risk Level</span>
          {selectedCount > 0 && (
            <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#131313] text-[12px] text-white">
              {selectedCount}
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {riskLevels.map((level) => (
          <div
            key={level.value}
            role="menuitemcheckbox"
            aria-checked={value.includes(level.value)}
            tabIndex={0}
            className="flex w-full cursor-pointer items-center gap-2 px-2 py-2 hover:bg-muted"
            onClick={() => handleToggle(level.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle(level.value);
              }
            }}
          >
            <Checkbox checked={value.includes(level.value)} tabIndex={-1} />
            <span>{level.label}</span>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
